<script setup>
/* global axios */
import { reactive, computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const uiFlags = useMapGetter('ailabInboxes/getUIFlags');
const ailabInboxes = useMapGetter('ailabInboxes/getRecords');

const isFetchingInboxes = ref(false);
const inboxRecords = ref([]);

const initialState = {
  inboxId: null,
};

const state = reactive({ ...initialState });

const validationRules = {
  inboxId: { required },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(
  () => uiFlags.value.creatingItem || isFetchingInboxes.value
);

const accountIdFromRoute = computed(() => {
  const parts = window.location.pathname.split('/');
  // /app/accounts/:id/...
  return parts?.[3] || '';
});

const availableInboxOptions = computed(() => {
  const connectedIds = (ailabInboxes.value || []).map(inbox =>
    Number(inbox.id)
  );
  return (inboxRecords.value || [])
    .filter(inbox => !connectedIds.includes(Number(inbox.id)))
    .map(inbox => ({ id: Number(inbox.id), name: inbox.name }));
});

const inboxError = computed(() => {
  return v$.value.inboxId.$error ? t('AILAB.INBOXES.FORM.INBOX.ERROR') : '';
});

const fetchInboxesFromNetwork = async () => {
  const accountId = accountIdFromRoute.value;
  if (!accountId) return;

  isFetchingInboxes.value = true;
  try {
    // Cache-buster avoids 304/conditional responses causing empty store on some browsers.
    const url = `/api/v1/accounts/${accountId}/inboxes?ts=${Date.now()}`;
    const response = await axios.get(url, {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    inboxRecords.value = response?.data?.payload || [];
  } catch {
    inboxRecords.value = [];
  } finally {
    isFetchingInboxes.value = false;
  }
};

onMounted(() => {
  fetchInboxesFromNetwork();
});

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  emit('submit', { inboxId: state.inboxId, assistantId: props.assistantId });
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('AILAB.INBOXES.FORM.INBOX.LABEL') }}
      </label>

      <select
        id="inbox"
        v-model="state.inboxId"
        class="w-full px-3 py-2.5 text-sm rounded-md border bg-n-alpha-black2 text-n-slate-12 border-n-strong focus:outline-none focus:ring-2 focus:ring-n-brand/30"
        :class="{
          'border-n-ruby-9 focus:ring-n-ruby-9/20': !!inboxError,
          'opacity-60 cursor-not-allowed': isLoading,
        }"
        :disabled="isLoading"
      >
        <option :value="null" disabled>
          {{ t('AILAB.INBOXES.FORM.INBOX.PLACEHOLDER') }}
        </option>
        <option
          v-for="opt in availableInboxOptions"
          :key="opt.id"
          :value="opt.id"
        >
          {{ opt.name }}
        </option>
      </select>

      <p v-if="inboxError" class="mt-2 mb-0 text-xs text-n-ruby-9 truncate">
        {{ inboxError }}
      </p>

      <p
        v-if="!isFetchingInboxes && availableInboxOptions.length === 0"
        class="mt-2 mb-0 text-xs text-n-slate-11"
      >
        {{ t('AILAB.INBOXES.FORM.NO_AVAILABLE') }}
      </p>
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('AILAB.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        :disabled="isLoading"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('AILAB.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading || availableInboxOptions.length === 0"
      />
    </div>
  </form>
</template>
